import { molarMass } from './formula';

/**
 * A small expression evaluator written by hand.
 *
 * Imported problem templates are data, never code. There is no `eval`, no
 * `Function`, no property access and no way to reach anything outside this
 * file. The grammar is numbers, variables, `+ - * / ^`, parentheses, and a
 * fixed list of functions — nothing else parses at all.
 */

export class ExpressionError extends Error {}

/** The only callable names. Anything else is rejected by the parser. */
export const ALLOWED_FUNCTIONS = [
  'sqrt',
  'ln',
  'log10',
  'exp',
  'abs',
  'round',
  'min',
  'max',
  'molarMass',
] as const;

export type FunctionName = (typeof ALLOWED_FUNCTIONS)[number];

const ARITY: Record<FunctionName, { min: number; max: number }> = {
  sqrt: { min: 1, max: 1 },
  ln: { min: 1, max: 1 },
  log10: { min: 1, max: 1 },
  exp: { min: 1, max: 1 },
  abs: { min: 1, max: 1 },
  round: { min: 1, max: 2 },
  min: { min: 1, max: 8 },
  max: { min: 1, max: 8 },
  molarMass: { min: 1, max: 1 },
};

export type Value = number | string;
export type Scope = Record<string, Value>;

// ------------------------------------------------------------------- tokens

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'name'; value: string }
  | { kind: 'op'; value: string }
  | { kind: '('; }
  | { kind: ')'; }
  | { kind: ','; };

const MAX_LENGTH = 2000;

export function tokenize(input: string): Token[] {
  if (input.length > MAX_LENGTH) throw new ExpressionError('That expression is too long.');
  const tokens: Token[] = [];
  let i = 0;

  while (i < input.length) {
    const ch = input[i]!;

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (/[0-9.]/.test(ch)) {
      let text = '';
      while (i < input.length && /[0-9.]/.test(input[i]!)) text += input[i++]!;
      // Scientific notation, but only where it is unambiguous: 6.022e23.
      if (i < input.length && /[eE]/.test(input[i]!) && /[0-9+-]/.test(input[i + 1] ?? '')) {
        text += input[i++]!;
        if (/[+-]/.test(input[i]!)) text += input[i++]!;
        while (i < input.length && /[0-9]/.test(input[i]!)) text += input[i++]!;
      }
      const value = Number(text);
      if (!Number.isFinite(value)) throw new ExpressionError(`"${text}" is not a number.`);
      tokens.push({ kind: 'number', value });
      continue;
    }

    if (/[A-Za-z_]/.test(ch)) {
      let text = '';
      while (i < input.length && /[A-Za-z0-9_]/.test(input[i]!)) text += input[i++]!;
      tokens.push({ kind: 'name', value: text });
      continue;
    }

    if ('+-*/^'.includes(ch)) {
      tokens.push({ kind: 'op', value: ch });
      i += 1;
      continue;
    }

    if (ch === '(') {
      tokens.push({ kind: '(' });
      i += 1;
      continue;
    }
    if (ch === ')') {
      tokens.push({ kind: ')' });
      i += 1;
      continue;
    }
    if (ch === ',') {
      tokens.push({ kind: ',' });
      i += 1;
      continue;
    }

    throw new ExpressionError(`"${ch}" is not allowed in an expression.`);
  }

  return tokens;
}

// -------------------------------------------------------------------- parse

export type Node =
  | { type: 'number'; value: number }
  | { type: 'variable'; name: string }
  | { type: 'unary'; op: '-'; operand: Node }
  | { type: 'binary'; op: '+' | '-' | '*' | '/' | '^'; left: Node; right: Node }
  | { type: 'call'; name: FunctionName; args: Node[] };

class Parser {
  private i = 0;

  constructor(private tokens: Token[]) {}

  parse(): Node {
    const node = this.expression();
    if (this.i < this.tokens.length) {
      throw new ExpressionError('There is something left over at the end of the expression.');
    }
    return node;
  }

  private peek(): Token | undefined {
    return this.tokens[this.i];
  }

  private expression(): Node {
    let left = this.term();
    for (;;) {
      const token = this.peek();
      if (token?.kind === 'op' && (token.value === '+' || token.value === '-')) {
        this.i += 1;
        left = { type: 'binary', op: token.value, left, right: this.term() };
      } else return left;
    }
  }

  private term(): Node {
    let left = this.unary();
    for (;;) {
      const token = this.peek();
      if (token?.kind === 'op' && (token.value === '*' || token.value === '/')) {
        this.i += 1;
        left = { type: 'binary', op: token.value, left, right: this.unary() };
      } else return left;
    }
  }

  private unary(): Node {
    const token = this.peek();
    if (token?.kind === 'op' && token.value === '-') {
      this.i += 1;
      return { type: 'unary', op: '-', operand: this.unary() };
    }
    if (token?.kind === 'op' && token.value === '+') {
      this.i += 1;
      return this.unary();
    }
    return this.power();
  }

  private power(): Node {
    const base = this.primary();
    const token = this.peek();
    if (token?.kind === 'op' && token.value === '^') {
      this.i += 1;
      // Right-associative, and the exponent may be negative.
      return { type: 'binary', op: '^', left: base, right: this.unary() };
    }
    return base;
  }

  private primary(): Node {
    const token = this.peek();
    if (!token) throw new ExpressionError('The expression ends too early.');

    if (token.kind === 'number') {
      this.i += 1;
      return { type: 'number', value: token.value };
    }

    if (token.kind === '(') {
      this.i += 1;
      const inner = this.expression();
      if (this.peek()?.kind !== ')') throw new ExpressionError('A "(" is never closed.');
      this.i += 1;
      return inner;
    }

    if (token.kind === 'name') {
      this.i += 1;
      if (this.peek()?.kind === '(') {
        const name = token.value;
        if (!(ALLOWED_FUNCTIONS as readonly string[]).includes(name)) {
          throw new ExpressionError(`"${name}" is not a function you can call.`);
        }
        this.i += 1;
        const args: Node[] = [];
        if (this.peek()?.kind !== ')') {
          for (;;) {
            args.push(this.expression());
            if (this.peek()?.kind === ',') {
              this.i += 1;
              continue;
            }
            break;
          }
        }
        if (this.peek()?.kind !== ')') throw new ExpressionError(`"${name}(" is never closed.`);
        this.i += 1;

        const arity = ARITY[name as FunctionName];
        if (args.length < arity.min || args.length > arity.max) {
          throw new ExpressionError(
            `${name} takes ${arity.min === arity.max ? arity.min : `${arity.min} to ${arity.max}`} argument${arity.max === 1 ? '' : 's'}, not ${args.length}.`,
          );
        }
        return { type: 'call', name: name as FunctionName, args };
      }
      return { type: 'variable', name: token.value };
    }

    throw new ExpressionError('The expression is malformed.');
  }
}

export function parseExpression(input: string): Node {
  return new Parser(tokenize(input)).parse();
}

/** Every variable an expression reads, so templates can be checked on import. */
export function variablesUsed(node: Node): Set<string> {
  const out = new Set<string>();
  const walk = (n: Node) => {
    if (n.type === 'variable') out.add(n.name);
    else if (n.type === 'unary') walk(n.operand);
    else if (n.type === 'binary') {
      walk(n.left);
      walk(n.right);
    } else if (n.type === 'call') n.args.forEach(walk);
  };
  walk(node);
  return out;
}

// ---------------------------------------------------------------- evaluate

function asNumber(value: Value, what: string): number {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new ExpressionError(`${what} is not a finite number.`);
    return value;
  }
  throw new ExpressionError(`${what} is text, and arithmetic needs a number.`);
}

export function evaluate(node: Node, scope: Scope = {}): number {
  switch (node.type) {
    case 'number':
      return node.value;

    case 'variable': {
      // Own properties only. `"__proto__" in {}` and `"constructor" in {}` are
      // both true through the prototype chain, which would hand an expression a
      // live object from the runtime.
      if (!Object.hasOwn(scope, node.name)) {
        throw new ExpressionError(`"${node.name}" has no value.`);
      }
      return asNumber(scope[node.name]!, `"${node.name}"`);
    }

    case 'unary':
      return -evaluate(node.operand, scope);

    case 'binary': {
      const left = evaluate(node.left, scope);
      const right = evaluate(node.right, scope);
      let result: number;
      switch (node.op) {
        case '+':
          result = left + right;
          break;
        case '-':
          result = left - right;
          break;
        case '*':
          result = left * right;
          break;
        case '/':
          if (right === 0) throw new ExpressionError('This divides by zero.');
          result = left / right;
          break;
        case '^':
          result = left ** right;
          break;
      }
      if (!Number.isFinite(result)) throw new ExpressionError('This works out to an impossible number.');
      return result;
    }

    case 'call': {
      // molarMass is the one function that takes a formula rather than a number.
      if (node.name === 'molarMass') {
        const arg = node.args[0]!;
        if (arg.type !== 'variable' && arg.type !== 'number') {
          throw new ExpressionError('molarMass takes a formula or a variable holding one.');
        }
        if (arg.type === 'number') return molarMass(String(arg.value));
        if (!Object.hasOwn(scope, arg.name)) {
          throw new ExpressionError(`"${arg.name}" has no value.`);
        }
        return molarMass(String(scope[arg.name]));
      }

      const args = node.args.map((a, i) => asNumber(evaluate(a, scope), `argument ${i + 1} of ${node.name}`));
      const [a = 0, b] = args;
      let result: number;
      switch (node.name) {
        case 'sqrt':
          if (a < 0) throw new ExpressionError('The square root of a negative number is not real.');
          result = Math.sqrt(a);
          break;
        case 'ln':
          if (a <= 0) throw new ExpressionError('ln needs a number above zero.');
          result = Math.log(a);
          break;
        case 'log10':
          if (a <= 0) throw new ExpressionError('log10 needs a number above zero.');
          result = Math.log10(a);
          break;
        case 'exp':
          result = Math.exp(a);
          break;
        case 'abs':
          result = Math.abs(a);
          break;
        case 'round': {
          const places = b === undefined ? 0 : Math.trunc(b);
          if (places < -20 || places > 20) throw new ExpressionError('round can only go to 20 places.');
          const factor = 10 ** places;
          result = Math.round(a * factor) / factor;
          break;
        }
        case 'min':
          result = Math.min(...args);
          break;
        case 'max':
          result = Math.max(...args);
          break;
      }
      if (!Number.isFinite(result)) throw new ExpressionError('This works out to an impossible number.');
      return result;
    }
  }
}

/** Parse and evaluate in one step. */
export function compute(expression: string, scope: Scope = {}): number {
  return evaluate(parseExpression(expression), scope);
}
